import epsClient from 'enrollment-platform/api/eps.client'

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum CarrierGroup {
  FLORIDA_BLUE = 'FLORIDA_BLUE',
  HARVARD_PILGRIM = 'HARVARD_PILGRIM',
  HIGHMARK_BCBS = 'HIGHMARK_BCBS',
}

export enum PlanBotTaskType {
  CARRIER_API = 'CARRIER_API',
}

export enum PlanBotTaskStatus {
  QUEUED = 'QUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  PARTIAL = 'PARTIAL',
  CANCELLED = 'CANCELLED',
}

export enum PlanBotTriggerSource {
  MANUAL = 'MANUAL',
  SCHEDULER = 'SCHEDULER',
}

export enum MismatchType {
  OK = 'OK',
  NOT_ON_CARRIER = 'NOT_ON_CARRIER',
  NOT_IN_DB = 'NOT_IN_DB',
  PRICE_CHANGED = 'PRICE_CHANGED',
}

// ─── Pagination ────────────────────────────────────────────────────────────────

export interface PaginationMeta {
  total: number
  page: number
  limit: number
  totalPages: number
  hasNextPage: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: PaginationMeta
}

// ─── Response DTOs ─────────────────────────────────────────────────────────────

/**
 * One ZIP-level work item within a state. Only returned by GET /tasks/:id
 * (summaryInfo[state].areas[]); absent on the GET /tasks list response.
 */
export interface TaskAreaEntry {
  zip: string
  status: string
  errorMessage: string | null
  errorCategory: string | null
}

export interface TaskAreaSummary {
  totalAreas?: number
  failedAreas?: number
  completedAreas?: number
  totalPlans?: number
  status?: string
  counties?: Record<string, { totalPlans: number }>
  areas?: TaskAreaEntry[]
}

export type TaskSummaryInfo = Record<string, TaskAreaSummary>

export interface PassProgressEntry {
  total: number
  queued: number
  inProgress: number
  completed: number
  failed: number
  plansFound: number
}

export interface TaskProgress {
  broadScan: PassProgressEntry
  standardScan: PassProgressEntry
  deepScan: PassProgressEntry
}

export interface TaskResponseDto {
  id: string
  groupCode: string
  year: number
  state: string[]
  issuerId: string[]
  carrierName: string | null
  type: PlanBotTaskType
  status: PlanBotTaskStatus
  summaryInfo: TaskSummaryInfo
  progress: TaskProgress | null
  errorMessage: string | null
  startedAt: string | null
  completedAt: string | null
  triggerSource: PlanBotTriggerSource
  triggeredBy: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Aggregates the three scan passes into a single 0–100 percent for display.
 *
 * Each pass (broad/standard/deep) is treated as an equal-weight stage
 * (~33% each) rather than weighting by raw item count. Later passes
 * typically cover far more ZIPs/plans than the broad pass, so a naive
 * item-weighted average gets stuck near 0% even after the broad pass is
 * nearly done. Passes that haven't started yet (total === 0, i.e. not
 * applicable to this task) are excluded from the average.
 *
 * Completed, inProgress, AND failed items count towards progress so the
 * bar advances as soon as a ZIP is picked up (rather than waiting for the
 * backend's batch-completion flush, ~100 ZIPs at a time) and doesn't move
 * backward once an in-progress ZIP is later flushed as failed.
 *
 * Returns null when there's no usable progress data at all (e.g. pre-v2
 * tasks), so callers can fall back to a legacy calculation.
 */
export function computeTaskProgressPercent(
  progress: TaskProgress | null | undefined
): number | null {
  if (!progress) return null
  const passes = [progress.broadScan, progress.standardScan, progress.deepScan]
  const passPercents = passes
    .filter((pass): pass is PassProgressEntry => !!pass && pass.total > 0)
    .map(
      (pass) =>
        ((pass.completed + pass.inProgress + pass.failed) / pass.total) * 100
    )
  if (passPercents.length === 0) return null
  const average =
    passPercents.reduce((sum, percent) => sum + percent, 0) /
    passPercents.length
  return Math.round(average)
}

/**
 * Sums the three scan passes (broad/standard/deep) into a single totals
 * object so the UI can show "how many ZIPs are left" instead of three
 * separate pass counters.
 *
 * Returns null when there's no usable progress data at all (e.g. pre-v2
 * tasks).
 */
export function sumTaskProgress(
  progress: TaskProgress | null | undefined
): PassProgressEntry | null {
  if (!progress) return null
  const passes = [progress.broadScan, progress.standardScan, progress.deepScan]
  const applicablePasses = passes.filter(
    (pass): pass is PassProgressEntry => !!pass && pass.total > 0
  )
  if (applicablePasses.length === 0) return null
  return applicablePasses.reduce(
    (sum, pass) => ({
      total: sum.total + pass.total,
      queued: sum.queued + pass.queued,
      inProgress: sum.inProgress + pass.inProgress,
      completed: sum.completed + pass.completed,
      failed: sum.failed + pass.failed,
      plansFound: sum.plansFound + pass.plansFound,
    }),
    {
      total: 0,
      queued: 0,
      inProgress: 0,
      completed: 0,
      failed: 0,
      plansFound: 0,
    }
  )
}

export interface TaskAreaTotals {
  totalAreas: number
  failedAreas: number
  completedAreas: number
}

export function sumTaskAreas(
  summaryInfo: TaskSummaryInfo | null | undefined
): TaskAreaTotals {
  let totalAreas = 0
  let failedAreas = 0
  let completedAreas = 0
  for (const summary of Object.values(summaryInfo ?? {})) {
    totalAreas += summary.totalAreas ?? 0
    failedAreas += summary.failedAreas ?? 0
    completedAreas += summary.completedAreas ?? 0
  }
  return { totalAreas, failedAreas, completedAreas }
}

export function formatTaskStates(state: string[] | null | undefined): string {
  if (!state?.length) return '—'
  return state.join(', ')
}

export type StateAreaStatus =
  | 'success'
  | 'failed'
  | 'partial'
  | 'pending'
  | 'cancelled'

export const STATE_AREA_STATUS_LABELS: Record<StateAreaStatus, string> = {
  success: 'Success',
  failed: 'Failed',
  partial: 'Partial',
  pending: 'Pending',
  cancelled: 'Cancelled',
}

export function getStateAreaStatus(
  summary: TaskAreaSummary | null | undefined,
  taskStatus?: PlanBotTaskStatus | null
): StateAreaStatus {
  if (!summary) {
    return taskStatus === PlanBotTaskStatus.CANCELLED ? 'cancelled' : 'pending'
  }
  const fromApi = mapFetchStatus(summary.status)
  if (fromApi) return fromApi
  // Some carrier integrations (e.g. simple CARRIER_API tasks) only report a
  // per-state totalPlans count with no area-level breakdown at all. Fall
  // back to that signal instead of comparing undefined area counts, which
  // would otherwise always look like "success" (undefined === undefined).
  const hasAreaCounts = typeof summary.totalAreas === 'number'
  if (hasAreaCounts) {
    if ((summary.totalAreas ?? 0) <= 0) return 'pending'
    if (summary.completedAreas === summary.totalAreas) return 'success'
    if ((summary.completedAreas ?? 0) > 0 && (summary.failedAreas ?? 0) > 0)
      return 'partial'
    if ((summary.failedAreas ?? 0) > 0) return 'failed'
    return 'pending'
  }
  if (typeof summary.totalPlans === 'number') {
    if (summary.totalPlans > 0) return 'success'
    // 0 plans with no per-state status or area breakdown is ambiguous: it
    // could mean the fetch genuinely came back empty, or it could mean the
    // task never got around to this state at all. Only call it "failed"
    // when the parent task actually ran to a terminal state on its own —
    // otherwise defer to the parent task's status instead of guessing.
    if (taskStatus === PlanBotTaskStatus.CANCELLED) return 'cancelled'
    if (
      taskStatus === PlanBotTaskStatus.QUEUED ||
      taskStatus === PlanBotTaskStatus.IN_PROGRESS
    )
      return 'pending'
    return 'failed'
  }
  return 'pending'
}

function mapFetchStatus(status?: string | null): StateAreaStatus | null {
  switch (status) {
    case 'COMPLETED':
      return 'success'
    case 'FAILED':
      return 'failed'
    case 'PARTIAL':
      return 'partial'
    case 'IN_PROGRESS':
    case 'QUEUED':
      return 'pending'
    case 'CANCELLED':
      return 'cancelled'
    default:
      return null
  }
}

/**
 * True when a per-ZIP work item (from GET /tasks/:id summaryInfo[state].areas[])
 * represents a failed scrape — either its status maps to "failed" or it carries
 * an error message/category. Used to paint failed ZIP rows red with an alert icon.
 */
export function isAreaZipFailed(entry: {
  status?: string | null
  errorMessage?: string | null
  errorCategory?: string | null
}): boolean {
  return (
    mapFetchStatus(entry.status) === 'failed' ||
    Boolean(entry.errorMessage) ||
    Boolean(entry.errorCategory)
  )
}

export type PlanBotScanStatus = 'COMPLETED' | 'PARTIAL' | 'FAILED'

/** One row per state from GET /tasks/:id/counties/summary. */
export interface TaskStateCountySummary {
  state: string
  totalPlans: number
  stateStatus: PlanBotScanStatus
}

export interface StateSummaryRow {
  state: string
  status: StateAreaStatus
  totalPlans: number
}

/**
 * Fallback builder used before GET /tasks/:id/counties/summary resolves: derives
 * the per-state rows from the slim summaryInfo carried on the list response.
 */
export function buildStateSummaryRows(
  states: string[] | null | undefined,
  summaryInfo: TaskSummaryInfo | null | undefined,
  taskStatus?: PlanBotTaskStatus | null
): StateSummaryRow[] {
  const keys = states?.length ? states : Object.keys(summaryInfo ?? {})
  return keys.map((state) => {
    const summary = summaryInfo?.[state]
    return {
      state,
      status: getStateAreaStatus(summary, taskStatus),
      totalPlans: summary?.totalPlans ?? 0,
    }
  })
}

/**
 * Builds one row per state from the GET /tasks/:id/counties/summary rollup —
 * the authoritative per-state totalPlans + COMPLETED/PARTIAL/FAILED status.
 */
export function buildStateRowsFromSummary(
  summaries: TaskStateCountySummary[] | null | undefined
): StateSummaryRow[] {
  if (!summaries?.length) return []
  return [...summaries]
    .sort((a, b) => a.state.localeCompare(b.state))
    .map((entry) => ({
      state: entry.state,
      status: mapFetchStatus(entry.stateStatus) ?? 'pending',
      totalPlans: entry.totalPlans ?? 0,
    }))
}

export function formatTaskFailureDetail(
  summaryInfo: TaskSummaryInfo | null | undefined,
  errorMessage?: string | null
): string | null {
  if (errorMessage) return errorMessage
  const parts: string[] = []
  for (const [state, info] of Object.entries(summaryInfo ?? {})) {
    const hasAreaCounts = typeof info.totalAreas === 'number'
    if (hasAreaCounts) {
      const failedAreas = info.failedAreas ?? 0
      if (failedAreas <= 0 && info.status !== 'FAILED') continue
      const areaErrors = Array.from(
        new Set(
          (info.areas ?? [])
            .map((area) => area.errorMessage)
            .filter((msg): msg is string => Boolean(msg))
        )
      )
      if (areaErrors.length > 0) {
        parts.push(`${state}: ${areaErrors.join('; ')}`)
      } else if (failedAreas > 0) {
        parts.push(`${state}: ${failedAreas} failed`)
      } else {
        parts.push(`${state}: task failed`)
      }
    } else if (typeof info.totalPlans === 'number' && info.totalPlans <= 0) {
      parts.push(`${state}: 0 plans returned`)
    }
  }
  if (parts.length > 0) return parts.join(', ')
  const { failedAreas } = sumTaskAreas(summaryInfo)
  if (failedAreas <= 0) return null
  return `${failedAreas} area${failedAreas !== 1 ? 's' : ''} failed`
}

export interface CrawlScope {
  broadScan: boolean
  standardScan: boolean
  deepScan: boolean
  testScan?: boolean
}

export interface CarrierSourceOption {
  id: string
  label: string
}

export interface CarrierResponseDto {
  id: string
  groupCode: string
  year: number
  carrierName: string | null
  carrierFullName: string | null
  baseUrl: string
  autoSchedule: boolean
  isActive: boolean
  remark: string | null
  createdAt: string
  isRunning: boolean
  latestTaskId: string | null
  latestTaskStatus: PlanBotTaskStatus | null
  latestTaskType: PlanBotTaskType | null
  latestTaskCreatedAt: string | null
  crawlScope?: CrawlScope
  /** Email addresses notified on task completion. Empty array = none configured. */
  notificationEmails: string[]
  /** Id of the site the next task fetches from; null when the config has none set. */
  source?: string | null
  /** Every site this carrier can be fetched from; a choice is offered only when there are several. */
  sources?: CarrierSourceOption[]
}

export interface CarrierFileResponseDto {
  id: string
  groupCode: string
  year: number
  isActive: boolean
  hasFile: boolean
  fileTaskId: string | null
  fileStatus: string | null
  fileGeneratedAt: string | null
}

export interface AssignmentResponseDto {
  hiosPlanId: string
  planName: string
  planType: string | null
  metalLevel: string | null
  hsaEligible: boolean
  issuerName: string | null
  networkName: string | null
  onExchange: boolean
  offExchange: boolean
  isForSale: boolean
  planStatus: string
  sbcLink: string | null
  id: string
  ratingArea: number
  zip: string
  fips: string | null
  premiumAge21: number
  deeplinkUrl: string | null
  createdAt: string
}

export interface CountyAreaInfo {
  fipsCode: string
  zipCode: string
  ratingArea: number
  totalPlans: number
  /** Per-ZIP scrape outcome (COMPLETED / FAILED / ...). */
  status?: string
  errorMessage?: string | null
  errorCategory?: string | null
}

export interface CountyResponseDto {
  countyName: string
  countyCode: string
  state: string
  totalAreas: number
  totalPlans: number
  areasInfos: CountyAreaInfo[]
  /** Scrape outcome for this county (GET /tasks/:id/counties). */
  status?: PlanBotScanStatus
  /** Scrape outcome for the whole state this county belongs to. */
  stateStatus?: PlanBotScanStatus
}

export interface VerifyResultResponseDto {
  id: string
  planBotTaskId: string
  hiosPlanId: string
  state: string
  year: number
  issuerId: string
  zip: string
  countyCode: string | null
  ratingArea: number
  checkedAt: string
  isAvailableOnCarrier: boolean
  premiumAge21Carrier: number | null
  isInOurDb: boolean
  baseRateOurDb: number | null
  mismatchType: MismatchType
  createdAt: string
}

export type PlanBotReportType =
  | 'comparison'
  | 'planAvailabilityByZip'
  | 'planBotPlansAndRates'
  | 'serviceAreas'

export interface TaskReportLinkDto {
  type: PlanBotReportType
  state: string
  key: string
  url: string
  expiresInSeconds: number
}

// ─── Request params / bodies ───────────────────────────────────────────────────

export interface GetCarriersParams {
  year?: number
  isActive?: boolean
  groupCode?: CarrierGroup
}

export interface GetCarrierFilesParams {
  year?: number
  isActive?: boolean
  groupCode?: CarrierGroup
}

export type CarrierTaskSortField =
  | 'createdAt'
  | 'updatedAt'
  | 'status'
  | 'state'
  | 'year'
  | 'groupCode'

export type SortOrder = 'ASC' | 'DESC'

export interface GetCarrierTasksParams {
  page?: number
  limit?: number
  state?: string
  year?: number
  status?: PlanBotTaskStatus
  groupCode?: CarrierGroup
  isRunning?: boolean
  sortBy?: CarrierTaskSortField
  order?: SortOrder
}

export interface GetTaskPlanRatesParams {
  page?: number
  limit?: number
}

export interface GetTaskVerifyResultsParams {
  page?: number
  limit?: number
  mismatchType?: MismatchType
}

export interface CreateCarrierTasksRequest {
  groupCode: CarrierGroup
}

export interface UpsertCarrierRequest {
  year: number
  groupCode: CarrierGroup
  baseUrl: string
  autoSchedule?: boolean
  isActive?: boolean
  remark?: string
  crawlScope?: CrawlScope
  /**
   * Email addresses to receive completion notifications. Send an empty array
   * to clear all emails; omit the field entirely to keep the existing value.
   */
  notificationEmails?: string[]
  /** One of the carrier's `sources` ids; omit to keep the current one. */
  source?: string
}

// ─── Carrier endpoints ─────────────────────────────────────────────────────────

function asCarrierList(
  data:
    | CarrierResponseDto[]
    | PaginatedResponse<CarrierResponseDto>
    | null
    | undefined
): CarrierResponseDto[] {
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.data)) return data.data
  return []
}

export async function getCarriers(
  params: GetCarriersParams
): Promise<CarrierResponseDto[]> {
  const { data } = await epsClient.get<
    CarrierResponseDto[] | PaginatedResponse<CarrierResponseDto>
  >('/api/v1/plan-bot/carriers', { params })
  return asCarrierList(data)
}

export async function getCarrierFiles(
  params: GetCarrierFilesParams
): Promise<CarrierFileResponseDto[]> {
  const { data } = await epsClient.get<CarrierFileResponseDto[]>(
    '/api/v1/plan-bot/carriers/files',
    { params }
  )
  return data
}

export async function upsertCarrier(
  body: UpsertCarrierRequest
): Promise<CarrierResponseDto> {
  const { data } = await epsClient.post<CarrierResponseDto>(
    '/api/v1/plan-bot/carriers',
    body
  )
  return data
}

// ─── Task endpoints ────────────────────────────────────────────────────────────

export async function createCarrierTasks(
  body: CreateCarrierTasksRequest
): Promise<TaskResponseDto[]> {
  const { data } = await epsClient.post<TaskResponseDto[]>(
    '/api/v1/plan-bot/tasks',
    body
  )
  return data
}

export async function getCarrierTasks(
  params: GetCarrierTasksParams
): Promise<PaginatedResponse<TaskResponseDto>> {
  const { data } = await epsClient.get<PaginatedResponse<TaskResponseDto>>(
    '/api/v1/plan-bot/tasks',
    { params }
  )
  return data
}

export interface GetTaskCountiesParams {
  state?: string
}

export async function getTaskCounties(
  id: string,
  params?: GetTaskCountiesParams
): Promise<CountyResponseDto[]> {
  const { data } = await epsClient.get<CountyResponseDto[]>(
    `/api/v1/plan-bot/tasks/${id}/counties`,
    { params }
  )
  return data
}

export async function getTaskCountiesSummary(
  id: string,
  params?: GetTaskCountiesParams
): Promise<TaskStateCountySummary[]> {
  const { data } = await epsClient.get<TaskStateCountySummary[]>(
    `/api/v1/plan-bot/tasks/${id}/counties/summary`,
    { params }
  )
  return data
}

export async function getTaskDetail(id: string): Promise<TaskResponseDto> {
  const { data } = await epsClient.get<TaskResponseDto>(
    `/api/v1/plan-bot/tasks/${id}`
  )
  return data
}

export async function cancelTask(id: string): Promise<void> {
  await epsClient.post(`/api/v1/plan-bot/tasks/${id}/cancel`)
}

export async function getTaskPlanRates(
  id: string,
  params: GetTaskPlanRatesParams
): Promise<PaginatedResponse<AssignmentResponseDto>> {
  const { data } = await epsClient.get<
    PaginatedResponse<AssignmentResponseDto>
  >(`/api/v1/plan-bot/tasks/${id}/plan-rates`, { params })
  return data
}

export async function getTaskVerifyResults(
  id: string,
  params: GetTaskVerifyResultsParams
): Promise<PaginatedResponse<VerifyResultResponseDto>> {
  const { data } = await epsClient.get<
    PaginatedResponse<VerifyResultResponseDto>
  >(`/api/v1/plan-bot/tasks/${id}/verify-results`, { params })
  return data
}

export async function getDownloadableCarrierTasks(
  groupCode: string
): Promise<PaginatedResponse<TaskResponseDto>> {
  const qs = new URLSearchParams()
  qs.append('status', PlanBotTaskStatus.COMPLETED)
  qs.append('status', PlanBotTaskStatus.PARTIAL)
  qs.append('groupCode', groupCode)
  qs.append('limit', '50')

  const { data } = await epsClient.get<PaginatedResponse<TaskResponseDto>>(
    `/api/v1/plan-bot/tasks?${qs.toString()}`
  )
  return data
}

export async function getTaskReports(
  taskId: string,
  state?: string
): Promise<TaskReportLinkDto[]> {
  const params = state ? { state } : undefined
  const { data } = await epsClient.get<TaskReportLinkDto[]>(
    `/api/v1/plan-bot/tasks/${taskId}/reports`,
    { params }
  )
  return Array.isArray(data) ? data : []
}

// ─── Export / report download endpoints ───────────────────────────────────────

export type CarrierDownloadType =
  | 'PLAN_AVAILABILITY_BY_ZIP'
  | 'COMPARISON_REPORT'
  | 'PLANS_AND_RATES'
  | 'SERVICE_AREAS'

const CARRIER_DOWNLOAD_ENDPOINTS: Record<CarrierDownloadType, string> = {
  PLAN_AVAILABILITY_BY_ZIP: 'plan-availability-by-zip/export',
  COMPARISON_REPORT: 'comparison/export',
  PLANS_AND_RATES: 'plans-and-rates/export',
  SERVICE_AREAS: 'service-areas/export',
}

const CARRIER_REPORT_TYPE: Partial<
  Record<CarrierDownloadType, PlanBotReportType>
> = {
  PLAN_AVAILABILITY_BY_ZIP: 'planAvailabilityByZip',
  COMPARISON_REPORT: 'comparison',
  PLANS_AND_RATES: 'planBotPlansAndRates',
  SERVICE_AREAS: 'serviceAreas',
}

export interface DownloadCarrierParams {
  taskId: string
  downloadType: CarrierDownloadType
  state?: string
}

/**
 * The task's states that have something to download: a state the task
 * fetched no plans for (`summaryInfo[state].totalPlans === 0`) is left out; a
 * state with no count (older tasks) stays.
 */
export function downloadableStates(
  task: Pick<TaskResponseDto, 'state' | 'summaryInfo'> | undefined
): string[] {
  return (task?.state ?? []).filter(
    (state) => task?.summaryInfo?.[state]?.totalPlans !== 0
  )
}

export const NO_PLANS_AVAILABLE_MESSAGE = 'No plans available'

/**
 * Whether a download failed because the task scraped no plans: no report was
 * archived, and the /export fallback answers 404 "No carrier data found".
 * Reads status defensively rather than via isAxiosError, which can return false
 * when the bundler resolves a second axios copy.
 */
export function isNoPlansAvailableError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return false
  }
  const { response } = error
  return (
    typeof response === 'object' &&
    response !== null &&
    'status' in response &&
    response.status === 404
  )
}

export async function downloadCarrierFile(
  params: DownloadCarrierParams
): Promise<void> {
  const expectedType = CARRIER_REPORT_TYPE[params.downloadType]

  if (expectedType) {
    const reports = await getTaskReports(params.taskId, params.state)
    const ofType = reports.filter((r) => r.type === expectedType)
    // With `?state=XX` the endpoint answers with that state's dedicated archive
    // when one exists and with the combined "ALL" archive otherwise — it
    // documents that substitution as equivalent content, not an error, so any
    // same-type entry it returns is the right file. Without a state the
    // response is unfiltered and lists every per-state archive, so only the
    // combined "ALL" archive may be used; anything else would hand back a
    // single state's data under a whole-task download.
    const match = params.state
      ? (ofType.find((r) => r.state === params.state) ?? ofType[0])
      : ofType.find((r) => r.state === 'ALL')

    if (match) {
      triggerUrlDownload(match.url)
      return
    }
  }

  // silent fallback: generate from DB via legacy /export endpoint
  const segment = CARRIER_DOWNLOAD_ENDPOINTS[params.downloadType]
  const qs = new URLSearchParams()
  if (params.state) qs.append('state', params.state)
  const query = qs.toString() ? `?${qs.toString()}` : ''
  const response = await epsClient.get(
    `/api/v1/plan-bot/tasks/${params.taskId}/${segment}${query}`,
    { responseType: 'blob' }
  )
  triggerBlobDownload(
    new Blob([response.data]),
    extractFilename(response.headers?.['content-disposition'])
  )
}

// ─── Carrier connection test ────────────────────────────────────────────────────

export type CarrierConnectionErrorCategory =
  | 'TIMEOUT'
  | 'CONNECTION_REFUSED'
  | 'HTTP_ERROR'
  | 'BOT_DETECTION'
  | 'GEO_BLOCKED'
  | 'SESSION_FAILED'
  | 'UNKNOWN'

export interface CarrierConnectionPlan {
  hiosId: string
  planName: string
  issuerId: string
  issuerName: string
  premium: number
  planType: string
  metalLevel: string
  hsaEligible: boolean
  networkName: string
  onExchange: boolean
  offExchange: boolean
  isForSale: boolean
  serviceAreaId: string
  planStatus: string
  sbcLink: string | null
  startDate: string
  endDate: string
  countyCode: string | null
  fips: string | null
}

export interface CarrierConnectionResponse {
  connected: boolean
  endpoint: string
  httpStatus: number | null
  responseTimeMs: number
  plansReturned: number
  rawResponse: string
  errorCategory: CarrierConnectionErrorCategory | null
  error: string | null
  requiresUsVpn: boolean
  plans: CarrierConnectionPlan[]
}

export async function getCarrierConnection(params: {
  groupCode: CarrierGroup
  zip: string
  fipsCode: string
}): Promise<CarrierConnectionResponse> {
  const { data } = await epsClient.get<CarrierConnectionResponse>(
    '/api/v1/plan-bot/carrier-connection',
    { params }
  )
  return data
}

// ─── Private helpers ───────────────────────────────────────────────────────────

const RFC5987_FILENAME = /filename\*=UTF-8''([^;]+)/i
const QUOTED_FILENAME = /filename="([^"]+)"/
const BARE_FILENAME = /filename=([^;]+)/

/**
 * Reads the name the backend chose for the file. The header carries the
 * carrier, year, state and timestamp, so it is the only source we trust —
 * returns undefined rather than guessing when it is unreadable.
 *
 * A cross-origin response only exposes the header to JS when the server sends
 * `Access-Control-Expose-Headers: Content-Disposition`, so undefined here
 * usually means the endpoint is missing that header, not that it sent no name.
 */
function extractFilename(disposition: unknown): string | undefined {
  if (typeof disposition !== 'string') return undefined
  const encoded = RFC5987_FILENAME.exec(disposition)?.[1]
  if (encoded) {
    try {
      return basename(decodeURIComponent(encoded))
    } catch {
      // Malformed percent-encoding — fall through to the plain `filename`.
    }
  }
  const plain =
    QUOTED_FILENAME.exec(disposition)?.[1] ??
    BARE_FILENAME.exec(disposition)?.[1]
  return plain ? basename(plain) : undefined
}

function basename(name: string): string | undefined {
  return name.trim().split(/[\\/]/).pop() || undefined
}

function triggerBlobDownload(blob: Blob, fileName: string | undefined): void {
  if (!fileName) {
    console.warn(
      'Plan-bot export sent no readable Content-Disposition; letting the browser name the download'
    )
  }
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  if (fileName) a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

// Uses an anchor click instead of window.open() so the download isn't at the
// mercy of popup blockers, which can silently swallow window.open() calls
// once the user-gesture token has expired after an intervening await.
function triggerUrlDownload(url: string): void {
  const a = document.createElement('a')
  a.href = url
  a.rel = 'noopener,noreferrer'
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}
